import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Check, X, Play,
  Globe, Smartphone, CreditCard, Package, BarChart3, Users,
  Crown, Upload, Rocket, Gift, Sparkles, Clock,
  Instagram, MessageCircle, Eye, TrendingUp, ShoppingBag,
  Share2, Palette, Image, IndianRupee,
  Truck
} from 'lucide-react';
import UniversalNavbar from '../components/UniversalNavbar';
import VFooter from '../components/VFooter';
import COLORS from '../utils/colors';

/* CloudFront base for all static assets */
const CDN = 'https://d3qvi06vol9hrb.cloudfront.net/static/media';

/* Template preview screenshots */
const tplMinimal = `${CDN}/template1-preview.a716e2154d93a137787b.png`;
const tplStreet = `${CDN}/template2-preview.6a18dd7646218f101ebd.png`;
const tplLuxury = `${CDN}/template3-preview.b72361b87739e0acd742.png`;
const tplVibrant = `${CDN}/template4-preview.759895de197e8d2f81bd.png`;
const tplTrendy = `${CDN}/template5-preview.ed38ed642e361a64ab78.png`;
const tplEthnic = `${CDN}/template6-preview.09a96c81d0f19b0138f6.png`;

/* Fashion stock photos */
const fashionRunway = `${CDN}/indian-fashion-runway.ed37650b6271a854f9bf.jpg`;
const fashionSaree = `${CDN}/indian-saree-model.6a7ad909b99be6533d46.jpg`;
/* fashionStudio and fashionBoutique available via CDN if needed */

/* ─── Typography ─── */
const SERIF = "'Playfair Display', Georgia, serif";
const SANS = "'Manrope', sans-serif";

/* ════════════════════════════════════════════════════════════════
   ANIMATION COMPONENTS — pure CSS, no framer-motion
   ════════════════════════════════════════════════════════════════ */

/* 1. Reveal — scroll-triggered via IntersectionObserver */
const Reveal = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.1, rootMargin: '-40px' }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} ${className}`}
      style={{ transitionDelay: `${delay * 1000}ms` }}
    >
      {children}
    </div>
  );
};

/* 2. Stagger container + child */
const StaggerWrap = ({ children, className = '' }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.1, rootMargin: '-40px' }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return (
    <div ref={ref} className={className}>
      {React.Children.map(children, (child, i) =>
        React.isValidElement(child)
          ? React.cloneElement(child, {
              className: `${child.props.className || ''} transition-all duration-700 ease-out ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`,
              style: { ...child.props.style, transitionDelay: `${i * 100}ms` }
            })
          : child
      )}
    </div>
  );
};

const StaggerChild = ({ children, className = '', style }) => (
  <div className={className} style={style}>{children}</div>
);

/* 3. Infinite Marquee — pure CSS */
const Marquee = ({ children }) => (
  <div className="overflow-hidden">
    <div className="flex animate-marquee">
      <div className="flex shrink-0">{children}</div>
      <div className="flex shrink-0">{children}</div>
    </div>
  </div>
);

/* 4. Section Heading — Playfair Display */
const Heading = ({ children, light = false, className = '' }) => (
  <h2
    className={`text-[2.25rem] sm:text-[3rem] lg:text-[3.75rem] ${className}`}
    style={{
      fontFamily: SERIF, fontWeight: 400, lineHeight: 1.1,
      letterSpacing: '-0.02em', color: light ? '#fff' : COLORS.TEXT_DARK,
    }}
  >
    {children}
  </h2>
);

/* ════════════════════════════════════════════════════════════════
   LANDING PAGE
   ════════════════════════════════════════════════════════════════ */
const LandingPage = () => {
  const [showVideoModal, setShowVideoModal] = useState(false);

  const socialProofItems = [
    { icon: ShoppingBag, text: '578+ Products Uploaded' },
    { icon: Users, text: '117+ Fashion Designers' },
    { icon: Sparkles, text: '25 Premium Templates' },
    { icon: Gift, text: '\u20B90 Setup Fee' },
    { icon: Clock, text: 'Live in 5 Minutes' },
    { icon: CreditCard, text: 'Razorpay Payments Built-in' },
  ];

  return (
    <div className="min-h-screen" style={{ fontFamily: SANS }}>
      <UniversalNavbar />

      {/* ══════════════════════════════════════════════
          HERO
      ══════════════════════════════════════════════ */}
      <section
        className="relative min-h-screen flex items-center pt-20 pb-16 px-4 sm:px-6 lg:px-8 overflow-hidden"
        style={{ backgroundColor: COLORS.LINEN }}
      >
        {/* Subtle dot grid */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, ${COLORS.MIDNIGHT}15 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }} />

        <div className="relative max-w-7xl mx-auto w-full animate-fadeIn">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Copy */}
            <div className="text-center lg:text-left pt-8 lg:pt-0">
              <div
                className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full text-[13px] font-semibold mb-10 animate-fadeUp"
                style={{ backgroundColor: `${COLORS.AUTUMN}15`, color: COLORS.AUTUMN, border: `1px solid ${COLORS.AUTUMN}25` }}
              >
                <Sparkles className="w-4 h-4" />
                Built for Fashion Brands
              </div>

              <h1
                style={{ fontFamily: SERIF, lineHeight: 1.0, letterSpacing: '-0.02em' }}
                className="text-[2.75rem] sm:text-[3.75rem] lg:text-[4.5rem] xl:text-[5.5rem] mb-8"
              >
                {['Your Fashion Brand', 'Deserves Its'].map((line, i) => (
                  <span className="block" key={i}>
                    <span
                      className="block animate-fadeUp"
                      style={{ fontWeight: 400, color: COLORS.MIDNIGHT, animationDelay: `${0.1 + i * 0.12}s` }}
                    >
                      {line}
                    </span>
                  </span>
                ))}
                <span className="block">
                  <span
                    className="block italic animate-fadeUp"
                    style={{ color: COLORS.AUTUMN, fontWeight: 400, animationDelay: '0.34s' }}
                  >
                    Own Stage
                  </span>
                </span>
              </h1>

              <p
                className="text-lg sm:text-xl mb-10 max-w-lg mx-auto lg:mx-0 animate-fadeUp"
                style={{ color: COLORS.TEXT_MUTED, lineHeight: 1.75, fontWeight: 400, animationDelay: '0.45s' }}
              >
                Stop selling through DMs. Get your own professional website
                at <span className="font-semibold" style={{ color: COLORS.MIDNIGHT }}>yourbrand.vasstr.com</span> — live in 5 minutes.
              </p>

              <div
                className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-10 animate-fadeUp"
                style={{ animationDelay: '0.55s' }}
              >
                <Link to="/Register">
                  <button
                    className="group relative px-8 py-4 rounded-xl font-bold text-white text-[15px] flex items-center gap-2.5 w-full sm:w-auto justify-center overflow-hidden hover:scale-[1.03] active:scale-[0.97] transition-transform duration-200"
                    style={{ backgroundColor: COLORS.AUTUMN, boxShadow: `0 16px 40px -8px ${COLORS.AUTUMN}40` }}
                  >
                    <span className="relative z-10 flex items-center gap-2.5">
                      Start Free — No Card Needed
                      <ArrowRight className="w-[18px] h-[18px] group-hover:translate-x-1.5 transition-transform duration-300" />
                    </span>
                  </button>
                </Link>
                <button
                  onClick={() => setShowVideoModal(true)}
                  className="px-8 py-4 rounded-xl font-semibold text-[15px] flex items-center gap-2.5 w-full sm:w-auto justify-center hover:scale-[1.03] active:scale-[0.97] transition-all duration-200"
                  style={{ color: COLORS.MIDNIGHT, border: `1px solid ${COLORS.MIDNIGHT}25`, backgroundColor: 'transparent' }}
                >
                  <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ border: `1.5px solid ${COLORS.MIDNIGHT}30` }}>
                    <Play className="w-3.5 h-3.5 ml-0.5" />
                  </div>
                  Watch Demo
                </button>
              </div>

              <div
                className="flex flex-wrap gap-6 text-[13px] font-medium justify-center lg:justify-start animate-fadeUp"
                style={{ color: COLORS.TEXT_MUTED, animationDelay: '0.65s' }}
              >
                {['Free 14-day trial', 'Live in 5 minutes', 'No coding needed'].map((t, i) => (
                  <span key={i} className="flex items-center gap-2">
                    <Check className="w-4 h-4" style={{ color: COLORS.AUTUMN_LIGHT }} />{t}
                  </span>
                ))}
              </div>
            </div>

            {/* Device Mockup */}
            <div
              className="relative hidden lg:block animate-fadeIn"
              style={{ animationDelay: '0.3s' }}
            >
              <div
                className="relative rounded-[16px] overflow-hidden hover:scale-[1.01] transition-transform duration-500"
                style={{ backgroundColor: '#1a1a1a', boxShadow: '0 60px 120px -30px rgba(0,0,0,0.6)' }}
              >
                <div className="flex items-center gap-1.5 px-4 py-3">
                  <div className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
                  <div className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
                  <div className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
                  <div className="flex-1 mx-8 bg-[#2a2a2a] rounded-md px-4 py-1.5 text-[11px] text-[#666] text-center font-medium" style={{ letterSpacing: '0.05em' }}>
                    meraki.vasstr.com
                  </div>
                </div>
                <img src={tplEthnic} alt="Vasstr store preview" className="w-full h-auto block" />
              </div>

              {/* Floating mobile */}
              <div
                className="absolute -bottom-8 -left-12 w-[130px] rounded-[20px] overflow-hidden animate-fadeIn"
                style={{ backgroundColor: '#1a1a1a', boxShadow: '0 40px 80px -20px rgba(0,0,0,0.5)', padding: '6px', animationDelay: '0.8s' }}
              >
                <div className="w-8 h-1 bg-[#333] rounded-full mx-auto mb-1" />
                <div className="rounded-[16px] overflow-hidden">
                  <img src={tplMinimal} alt="Mobile preview" className="w-full h-auto block" />
                </div>
              </div>

              {/* Floating badge — Store Live */}
              <div
                className="absolute -top-4 -right-4 backdrop-blur-xl rounded-2xl px-4 py-3 flex items-center gap-3 animate-fadeUp"
                style={{ backgroundColor: 'rgba(255,255,255,0.95)', boxShadow: '0 20px 50px -12px rgba(0,0,0,0.15)', animationDelay: '1s' }}
              >
                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${COLORS.AUTUMN}12` }}>
                  <Check className="w-5 h-5" style={{ color: COLORS.AUTUMN }} />
                </div>
                <div>
                  <div className="text-[13px] font-bold" style={{ color: COLORS.TEXT_DARK }}>Store is Live!</div>
                  <div className="text-[11px] font-medium" style={{ color: COLORS.TEXT_MUTED }}>Ready to sell</div>
                </div>
              </div>

              {/* Floating badge — New Order */}
              <div
                className="absolute bottom-24 -right-6 backdrop-blur-xl rounded-2xl px-4 py-3 flex items-center gap-3 animate-fadeUp"
                style={{ backgroundColor: 'rgba(255,255,255,0.95)', boxShadow: '0 20px 50px -12px rgba(0,0,0,0.15)', animationDelay: '1.3s' }}
              >
                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${COLORS.AUTUMN}12` }}>
                  <Package className="w-5 h-5" style={{ color: COLORS.AUTUMN }} />
                </div>
                <div>
                  <div className="text-[13px] font-bold" style={{ color: COLORS.TEXT_DARK }}>New Order!</div>
                  <div className="text-[11px] font-medium" style={{ color: COLORS.TEXT_MUTED }}>Just now</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          SOCIAL PROOF — Infinite Marquee
      ══════════════════════════════════════════════ */}
      <section className="py-5 sm:py-6" style={{ backgroundColor: COLORS.LINEN }}>
        <Marquee>
          {socialProofItems.map((item, i) => (
            <div key={i} className="flex items-center gap-2.5 px-8 shrink-0">
              <item.icon className="w-[16px] h-[16px]" style={{ color: COLORS.AUTUMN }} />
              <span className="text-[13px] font-semibold whitespace-nowrap" style={{ color: COLORS.TEXT_MUTED, letterSpacing: '0.03em' }}>
                {item.text}
              </span>
              <span className="ml-6 text-[8px]" style={{ color: COLORS.AUTUMN, opacity: 0.35 }}>&#9670;</span>
            </div>
          ))}
        </Marquee>
      </section>

      {/* ══════════════════════════════════════════════
          SOUND FAMILIAR — Before / After
      ══════════════════════════════════════════════ */}
      <section className="py-24 sm:py-32 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#fff' }}>
        <div className="max-w-6xl mx-auto">
          <Reveal className="text-center mb-16">
            <Heading>Sound Familiar?</Heading>
            <p className="text-lg mt-5 max-w-2xl mx-auto" style={{ color: COLORS.TEXT_MUTED, lineHeight: 1.7 }}>
              Most fashion designers are stuck running their entire business through Instagram DMs
            </p>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-8 max-w-4xl mx-auto">
            {/* Without */}
            <Reveal delay={0.1}>
              <div className="rounded-2xl p-7 sm:p-9 h-full" style={{ backgroundColor: COLORS.LINEN }}>
                <div className="text-[11px] font-bold uppercase mb-6" style={{ letterSpacing: '0.15em', color: COLORS.TEXT_MUTED }}>
                  Without Vasstr
                </div>
                <ul className="space-y-4">
                  {[
                    'Managing orders through WhatsApp/Instagram DMs',
                    'No proper product catalog \u2014 sending photos one by one',
                    "Customers can't browse or pay on their own",
                    'Losing track of orders and payments',
                    'Looking unprofessional compared to bigger brands',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <X className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: COLORS.TEXT_MUTED }} />
                      <span className="text-[15px]" style={{ color: COLORS.TEXT_MUTED, lineHeight: 1.6 }}>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>

            {/* With */}
            <Reveal delay={0.2}>
              <div className="rounded-2xl p-7 sm:p-9 h-full" style={{ backgroundColor: '#fff', border: `2px solid ${COLORS.AUTUMN}20` }}>
                <div className="text-[11px] font-bold uppercase mb-6" style={{ letterSpacing: '0.15em', color: COLORS.AUTUMN }}>
                  With Vasstr
                </div>
                <ul className="space-y-4">
                  {[
                    'Professional storefront at yourbrand.vasstr.com',
                    'Beautiful product catalog customers can browse 24/7',
                    'Customers order and pay directly \u2014 no back and forth',
                    'Dashboard to track every order, payment & customer',
                    'You look like an established brand from Day 1',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: COLORS.AUTUMN }} />
                      <span className="text-[15px]" style={{ color: COLORS.TEXT_DARK, lineHeight: 1.6 }}>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          HOW IT WORKS
      ══════════════════════════════════════════════ */}
      <section className="relative py-24 sm:py-32 px-4 sm:px-6 lg:px-8 overflow-hidden" style={{ backgroundColor: '#fff' }}>
        <div className="relative max-w-6xl mx-auto">
          <Reveal className="text-center mb-16">
            <div
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[13px] font-semibold mb-6 hover:scale-105 transition-transform duration-200"
              style={{ backgroundColor: `${COLORS.AUTUMN}12`, color: COLORS.AUTUMN, border: `1px solid ${COLORS.AUTUMN}20` }}
            >
              Takes 5 Minutes
            </div>
            <Heading>Three Steps. That's It.</Heading>
            <p className="text-lg mt-5" style={{ color: COLORS.TEXT_MUTED, lineHeight: 1.7 }}>
              No technical skills needed. Seriously.
            </p>
          </Reveal>

          <StaggerWrap className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {[
              { step: '01', icon: Crown, title: 'Sign Up & Pick a Template', desc: 'Create your account, choose from 25 fashion-ready templates. Your branded URL goes live instantly.' },
              { step: '02', icon: Upload, title: 'Upload Your Designs', desc: 'Add photos, prices, and descriptions \u2014 as easy as posting on Instagram. Products go live immediately.' },
              { step: '03', icon: Rocket, title: 'Share & Start Selling', desc: 'Share your website link. Customers browse, order, and pay directly. You manage everything from one dashboard.' },
            ].map((item, i) => (
              <StaggerChild key={i}>
                <div
                  className="rounded-2xl p-7 sm:p-8 h-full cursor-default hover:-translate-y-2 hover:shadow-xl transition-all duration-300"
                  style={{ backgroundColor: COLORS.LINEN, border: `1px solid ${COLORS.AUTUMN}08` }}
                >
                  <div className="flex items-center justify-between mb-6">
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: `${COLORS.AUTUMN}15` }}
                    >
                      <item.icon className="w-5 h-5" style={{ color: COLORS.AUTUMN }} />
                    </div>
                    <span
                      className="text-[3rem]"
                      style={{ fontFamily: SERIF, fontWeight: 300, color: `${COLORS.AUTUMN}12` }}
                    >
                      {item.step}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold mb-3" style={{ letterSpacing: '-0.01em', lineHeight: 1.25, color: COLORS.TEXT_DARK }}>
                    {item.title}
                  </h3>
                  <p className="text-[15px]" style={{ color: COLORS.TEXT_MUTED, lineHeight: 1.7 }}>{item.desc}</p>
                </div>
              </StaggerChild>
            ))}
          </StaggerWrap>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          JOURNEY — Visual Process Walkthrough
      ══════════════════════════════════════════════ */}
      <section className="py-24 sm:py-32 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#fff' }}>
        <div className="max-w-6xl mx-auto">
          <Reveal className="text-center mb-20">
            <Heading>
              From Sign Up to<br />
              <span style={{ color: COLORS.AUTUMN, fontStyle: 'italic' }}>Your First Sale</span>
            </Heading>
            <p className="text-lg mt-5 max-w-2xl mx-auto" style={{ color: COLORS.TEXT_MUTED, lineHeight: 1.7 }}>
              Here's exactly what happens when you join Vasstr — see every step before you start
            </p>
          </Reveal>

          <div className="space-y-20 lg:space-y-28">
            {/* Step 1 — Sign Up */}
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-center">
              <Reveal className="flex-1 text-center lg:text-left">
                <div
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[12px] font-bold mb-5 hover:scale-105 transition-transform duration-200"
                  style={{ backgroundColor: `${COLORS.AUTUMN}12`, color: COLORS.AUTUMN }}
                >
                  Step 1 · 30 seconds
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold mb-4" style={{ fontFamily: SERIF, color: COLORS.TEXT_DARK }}>
                  Create Your Account
                </h3>
                <p className="text-[16px] max-w-md mx-auto lg:mx-0 mb-6" style={{ color: COLORS.TEXT_MUTED, lineHeight: 1.75 }}>
                  Enter your brand name and email. That's it — no credit card, no complex forms. You're in within 30 seconds.
                </p>
                <div className="flex flex-wrap gap-3 justify-center lg:justify-start">
                  {['No card needed', 'Instant access', 'Free for 14 days'].map((t, i) => (
                    <span key={i} className="flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-full"
                      style={{ backgroundColor: `${COLORS.AUTUMN}08`, color: COLORS.AUTUMN }}>
                      <Check className="w-3 h-3" /> {t}
                    </span>
                  ))}
                </div>
              </Reveal>

              <Reveal delay={0.15} className="flex-1 w-full max-w-lg">
                <div
                  className="rounded-2xl p-6 sm:p-8 hover:-translate-y-1 transition-transform duration-300"
                  style={{ backgroundColor: COLORS.LINEN, border: `1px solid ${COLORS.AUTUMN}10` }}
                >
                  <div className="text-[13px] font-bold mb-5" style={{ color: COLORS.TEXT_DARK }}>Start your fashion brand website</div>
                  <div className="space-y-3 mb-5">
                    {['Brand Name', 'Email Address', 'Password'].map((label, i) => (
                      <div key={i}>
                        <div className="text-[11px] font-semibold mb-1.5" style={{ color: COLORS.TEXT_MUTED }}>{label}</div>
                        <div className="bg-white rounded-lg h-10 px-3 flex items-center" style={{ border: '1px solid #e5e5e5' }}>
                          <span className="text-[13px]" style={{ color: i === 0 ? COLORS.TEXT_DARK : '#ccc' }}>
                            {i === 0 ? 'Meraki Fashions' : i === 1 ? 'priya@meraki.com' : '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div
                    className="w-full py-3 rounded-lg text-center text-[13px] font-bold text-white hover:scale-[1.02] transition-transform duration-200 cursor-pointer"
                    style={{ backgroundColor: COLORS.AUTUMN }}
                  >
                    Create My Store →
                  </div>
                  <div className="text-center text-[11px] mt-3" style={{ color: COLORS.TEXT_MUTED }}>
                    Your store: <span className="font-semibold" style={{ color: COLORS.AUTUMN }}>merakifashions.vasstr.com</span>
                  </div>
                </div>
              </Reveal>
            </div>

            {/* Step 2 — Pick Template */}
            <div className="flex flex-col lg:flex-row-reverse gap-10 lg:gap-16 items-center">
              <Reveal className="flex-1 text-center lg:text-left">
                <div
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[12px] font-bold mb-5 hover:scale-105 transition-transform duration-200"
                  style={{ backgroundColor: `${COLORS.AUTUMN}12`, color: COLORS.AUTUMN }}
                >
                  Step 2 · 1 minute
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold mb-4" style={{ fontFamily: SERIF, color: COLORS.TEXT_DARK }}>
                  Pick Your Look
                </h3>
                <p className="text-[16px] max-w-md mx-auto lg:mx-0 mb-6" style={{ color: COLORS.TEXT_MUTED, lineHeight: 1.75 }}>
                  Browse 25 professionally designed templates made specifically for Indian fashion brands. One click to apply — your website updates instantly.
                </p>
                <div className="flex flex-wrap gap-3 justify-center lg:justify-start">
                  {['25 templates', 'Fashion-focused', 'One-click apply'].map((t, i) => (
                    <span key={i} className="flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-full"
                      style={{ backgroundColor: `${COLORS.AUTUMN}08`, color: COLORS.AUTUMN }}>
                      <Palette className="w-3 h-3" /> {t}
                    </span>
                  ))}
                </div>
              </Reveal>

              <Reveal delay={0.15} className="flex-1 w-full max-w-lg">
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { img: tplMinimal, name: 'Minimal' },
                    { img: tplEthnic, name: 'Ethnic' },
                    { img: tplVibrant, name: 'Vibrant' },
                  ].map((tmpl, i) => (
                    <div
                      key={i}
                      className="rounded-xl overflow-hidden cursor-pointer relative hover:-translate-y-1 hover:scale-[1.03] transition-all duration-300"
                      style={{
                        border: i === 0 ? `2px solid ${COLORS.AUTUMN}` : '2px solid transparent',
                        boxShadow: i === 0 ? `0 8px 25px -6px ${COLORS.AUTUMN}30` : '0 4px 12px -4px rgba(0,0,0,0.1)',
                      }}
                    >
                      <div className="aspect-[3/4] overflow-hidden">
                        <img src={tmpl.img} alt={tmpl.name} className="w-full h-full object-cover object-top" />
                      </div>
                      <div className="p-2 text-center bg-white">
                        <div className="text-[11px] font-bold" style={{ color: COLORS.TEXT_DARK }}>{tmpl.name}</div>
                      </div>
                      {i === 0 && (
                        <div
                          className="absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: COLORS.AUTUMN }}
                        >
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>

            {/* Step 3 — Add Products */}
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-center">
              <Reveal className="flex-1 text-center lg:text-left">
                <div
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[12px] font-bold mb-5 hover:scale-105 transition-transform duration-200"
                  style={{ backgroundColor: `${COLORS.AUTUMN}12`, color: COLORS.AUTUMN }}
                >
                  Step 3 · 2 minutes per product
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold mb-4" style={{ fontFamily: SERIF, color: COLORS.TEXT_DARK }}>
                  Upload Your Designs
                </h3>
                <p className="text-[16px] max-w-md mx-auto lg:mx-0 mb-6" style={{ color: COLORS.TEXT_MUTED, lineHeight: 1.75 }}>
                  Add photos, set prices, add size options — as easy as posting on Instagram. Products go live on your store instantly.
                </p>
                <div className="flex flex-wrap gap-3 justify-center lg:justify-start">
                  {['Drag & drop upload', 'Multiple sizes', 'Instant publish'].map((t, i) => (
                    <span key={i} className="flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-full"
                      style={{ backgroundColor: `${COLORS.AUTUMN}08`, color: COLORS.AUTUMN }}>
                      <Image className="w-3 h-3" /> {t}
                    </span>
                  ))}
                </div>
              </Reveal>

              <Reveal delay={0.15} className="flex-1 w-full max-w-lg">
                <div
                  className="rounded-2xl p-5 sm:p-6 hover:-translate-y-1 transition-transform duration-300"
                  style={{ backgroundColor: COLORS.LINEN, border: `1px solid ${COLORS.AUTUMN}10` }}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-[13px] font-bold" style={{ color: COLORS.TEXT_DARK }}>Add New Product</div>
                    <div className="text-[11px] font-semibold px-2.5 py-1 rounded-full" style={{ backgroundColor: `${COLORS.AUTUMN}12`, color: COLORS.AUTUMN }}>Draft</div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    <div className="aspect-square rounded-lg overflow-hidden">
                      <img src={fashionRunway} alt="Product" className="w-full h-full object-cover" />
                    </div>
                    <div className="aspect-square rounded-lg overflow-hidden">
                      <img src={fashionSaree} alt="Product" className="w-full h-full object-cover" />
                    </div>
                    <div
                      className="aspect-square rounded-lg flex flex-col items-center justify-center cursor-pointer hover:scale-105 transition-transform duration-200"
                      style={{ border: `2px dashed ${COLORS.AUTUMN}40`, backgroundColor: 'white' }}
                    >
                      <Upload className="w-5 h-5 mb-1" style={{ color: COLORS.AUTUMN }} />
                      <span className="text-[9px] font-semibold" style={{ color: COLORS.AUTUMN }}>Add More</span>
                    </div>
                  </div>
                  <div className="space-y-2.5 mb-4">
                    <div className="bg-white rounded-lg h-9 px-3 flex items-center" style={{ border: '1px solid #e5e5e5' }}>
                      <span className="text-[12px] font-medium" style={{ color: COLORS.TEXT_DARK }}>Silk Embroidered Kurti Set</span>
                    </div>
                    <div className="flex gap-2">
                      <div className="bg-white rounded-lg h-9 px-3 flex items-center flex-1" style={{ border: '1px solid #e5e5e5' }}>
                        <span className="text-[12px] font-medium" style={{ color: COLORS.TEXT_DARK }}>\u20B92,450</span>
                      </div>
                      <div className="bg-white rounded-lg h-9 px-3 flex items-center flex-1" style={{ border: '1px solid #e5e5e5' }}>
                        <span className="text-[12px]" style={{ color: COLORS.TEXT_MUTED }}>S, M, L, XL</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <div className="flex-1 py-2.5 rounded-lg text-center text-[12px] font-semibold"
                      style={{ border: `1px solid ${COLORS.AUTUMN}`, color: COLORS.AUTUMN }}>
                      Save Draft
                    </div>
                    <div
                      className="flex-1 py-2.5 rounded-lg text-center text-[12px] font-bold text-white hover:scale-[1.02] transition-transform duration-200 cursor-pointer"
                      style={{ backgroundColor: COLORS.AUTUMN }}
                    >
                      Publish →
                    </div>
                  </div>
                </div>
              </Reveal>
            </div>

            {/* Step 4 — Share & Sell */}
            <div className="flex flex-col lg:flex-row-reverse gap-10 lg:gap-16 items-center">
              <Reveal className="flex-1 text-center lg:text-left">
                <div
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[12px] font-bold mb-5 hover:scale-105 transition-transform duration-200"
                  style={{ backgroundColor: `${COLORS.AUTUMN}12`, color: COLORS.AUTUMN }}
                >
                  Step 4 · You're live!
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold mb-4" style={{ fontFamily: SERIF, color: COLORS.TEXT_DARK }}>
                  Share & Start Earning
                </h3>
                <p className="text-[16px] max-w-md mx-auto lg:mx-0 mb-6" style={{ color: COLORS.TEXT_MUTED, lineHeight: 1.75 }}>
                  Your store is live! Share the link on Instagram bio, WhatsApp stories, anywhere. Customers browse, order, and pay directly — money goes straight to your bank.
                </p>
                <div className="flex flex-wrap gap-3 justify-center lg:justify-start">
                  {['Share anywhere', 'Direct payments', 'Track orders'].map((t, i) => (
                    <span key={i} className="flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-full"
                      style={{ backgroundColor: `${COLORS.AUTUMN}08`, color: COLORS.AUTUMN }}>
                      <Share2 className="w-3 h-3" /> {t}
                    </span>
                  ))}
                </div>
              </Reveal>

              <Reveal delay={0.15} className="flex-1 w-full max-w-lg">
                <div className="rounded-2xl p-5 sm:p-6 hover:-translate-y-1 transition-transform duration-300" style={{ backgroundColor: COLORS.LINEN, border: `1px solid ${COLORS.AUTUMN}10` }}>
                  <div className="text-[13px] font-bold mb-4" style={{ color: COLORS.TEXT_DARK }}>Your store is ready!</div>
                  {/* Link bar */}
                  <div
                    className="bg-white rounded-lg px-4 py-3 flex items-center justify-between mb-5"
                    style={{ border: `2px solid ${COLORS.AUTUMN}25` }}
                  >
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4" style={{ color: COLORS.AUTUMN }} />
                      <span className="text-[13px] font-semibold" style={{ color: COLORS.TEXT_DARK }}>merakifashions.vasstr.com</span>
                    </div>
                    <div
                      className="px-3 py-1.5 rounded-md text-[11px] font-bold text-white cursor-pointer hover:scale-105 transition-transform duration-200"
                      style={{ backgroundColor: COLORS.AUTUMN }}
                    >
                      Copy
                    </div>
                  </div>
                  {/* Share options */}
                  <div className="text-[11px] font-semibold mb-3" style={{ color: COLORS.TEXT_MUTED, letterSpacing: '0.05em' }}>SHARE ON</div>
                  <div className="flex gap-2 mb-5">
                    {[
                      { name: 'Instagram Bio', icon: Instagram, bg: '#E4405F' },
                      { name: 'WhatsApp', icon: MessageCircle, bg: '#25D366' },
                      { name: 'Facebook', icon: Share2, bg: '#1877F2' },
                    ].map((s, i) => (
                      <div
                        key={i}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-white text-[11px] font-bold cursor-pointer hover:scale-[1.03] hover:-translate-y-0.5 transition-all duration-200"
                        style={{ backgroundColor: s.bg }}
                      >
                        <s.icon className="w-3.5 h-3.5" />
                        {s.name}
                      </div>
                    ))}
                  </div>
                  {/* Notification */}
                  <div
                    className="bg-white rounded-lg px-4 py-3 flex items-center gap-3"
                    style={{ boxShadow: '0 4px 12px -4px rgba(0,0,0,0.08)' }}
                  >
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: '#10b98115' }}
                    >
                      <ShoppingBag className="w-4 h-4" style={{ color: '#10b981' }} />
                    </div>
                    <div>
                      <div className="text-[12px] font-bold" style={{ color: COLORS.TEXT_DARK }}>New Order! Priya M. placed an order</div>
                      <div className="text-[10px]" style={{ color: COLORS.TEXT_MUTED }}>Silk Embroidered Kurti Set · \u20B92,450</div>
                    </div>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          DASHBOARD PREVIEW — Your Command Center
      ══════════════════════════════════════════════ */}
      <section className="py-24 sm:py-32 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: COLORS.LINEN }}>
        <div className="max-w-6xl mx-auto">
          <Reveal className="text-center mb-16">
            <Heading>
              Your Business,<br />
              <span style={{ color: COLORS.AUTUMN, fontStyle: 'italic' }}>At a Glance</span>
            </Heading>
            <p className="text-lg mt-5 max-w-2xl mx-auto" style={{ color: COLORS.TEXT_MUTED, lineHeight: 1.7 }}>
              Track revenue, manage orders, and understand your customers — all from one powerful dashboard
            </p>
          </Reveal>

          {/* Dashboard Mockup in Browser Frame */}
          <Reveal>
            <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: '#1a1a1a', boxShadow: '0 60px 120px -30px rgba(0,0,0,0.25)' }}>
              {/* Browser chrome */}
              <div className="flex items-center gap-1.5 px-4 py-3">
                <div className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
                <div className="flex-1 mx-8 bg-[#2a2a2a] rounded-md px-4 py-1.5 text-[11px] text-[#666] text-center font-medium" style={{ letterSpacing: '0.05em' }}>
                  vasstr.com/dashboard
                </div>
              </div>

              {/* Dashboard content */}
              <div className="p-4 sm:p-6 lg:p-8" style={{ backgroundColor: '#f8f9fb' }}>
                {/* Greeting */}
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <div className="text-[15px] sm:text-[17px] font-bold" style={{ color: COLORS.TEXT_DARK }}>Good morning, Priya! &#128075;</div>
                    <div className="text-[12px]" style={{ color: COLORS.TEXT_MUTED }}>Here's how your store is performing</div>
                  </div>
                  <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg text-[11px] font-semibold"
                    style={{ backgroundColor: '#10b98112', color: '#10b981' }}>
                    <div className="w-1.5 h-1.5 rounded-full bg-[#10b981]" />
                    Store is Live
                  </div>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
                  {[
                    { label: 'Total Revenue', value: '\u20B91,84,500', trend: '+23%', icon: IndianRupee, color: COLORS.AUTUMN },
                    { label: 'Total Orders', value: '127', trend: '+18%', icon: ShoppingBag, color: '#10b981' },
                    { label: 'Active Customers', value: '89', trend: '+12%', icon: Users, color: '#3b82f6' },
                    { label: 'Conversion Rate', value: '4.2%', trend: '+2.1%', icon: TrendingUp, color: '#8b5cf6' },
                  ].map((stat, i) => (
                    <div
                      key={i}
                      className="bg-white rounded-xl p-4 sm:p-5 hover:-translate-y-0.5 hover:shadow-md transition-all duration-200"
                      style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="text-[11px] font-semibold" style={{ color: COLORS.TEXT_MUTED, letterSpacing: '0.02em' }}>{stat.label}</div>
                        <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${stat.color}12` }}>
                          <stat.icon className="w-3.5 h-3.5" style={{ color: stat.color }} />
                        </div>
                      </div>
                      <div className="text-xl sm:text-2xl font-extrabold mb-1" style={{ color: COLORS.TEXT_DARK, letterSpacing: '-0.02em' }}>
                        {stat.value}
                      </div>
                      <div className="flex items-center gap-1 text-[11px] font-semibold" style={{ color: stat.color }}>
                        <TrendingUp className="w-3 h-3" />
                        {stat.trend} this month
                      </div>
                    </div>
                  ))}
                </div>

                {/* Chart + Recent Orders */}
                <div className="grid lg:grid-cols-5 gap-4 sm:gap-5">
                  {/* Revenue Chart */}
                  <div
                    className="lg:col-span-3 bg-white rounded-xl p-5"
                    style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
                  >
                    <div className="flex items-center justify-between mb-5">
                      <div className="text-[13px] font-bold" style={{ color: COLORS.TEXT_DARK }}>Revenue Overview</div>
                      <div className="flex gap-1.5">
                        {['6M', '1Y', 'All'].map((p, i) => (
                          <div key={i} className="text-[10px] font-semibold px-2.5 py-1 rounded-md cursor-pointer"
                            style={i === 1 ? { backgroundColor: `${COLORS.AUTUMN}12`, color: COLORS.AUTUMN } : { color: COLORS.TEXT_MUTED }}>
                            {p}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-end gap-[6px] sm:gap-2 h-28 sm:h-36">
                      {[35, 52, 45, 68, 42, 78, 65, 85, 72, 92, 88, 95].map((h, i) => (
                        <div
                          key={i}
                          className="flex-1 rounded-t-md cursor-pointer relative group hover:opacity-90 transition-opacity duration-200"
                          style={{ backgroundColor: i === 11 ? COLORS.AUTUMN : `${COLORS.AUTUMN}25`, height: `${h}%` }}
                        />
                      ))}
                    </div>
                    <div className="flex justify-between mt-3 text-[10px] font-medium" style={{ color: COLORS.TEXT_MUTED }}>
                      {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map(m => (
                        <span key={m}>{m}</span>
                      ))}
                    </div>
                  </div>

                  {/* Recent Orders */}
                  <div
                    className="lg:col-span-2 bg-white rounded-xl p-5"
                    style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="text-[13px] font-bold" style={{ color: COLORS.TEXT_DARK }}>Recent Orders</div>
                      <div className="text-[11px] font-semibold cursor-pointer" style={{ color: COLORS.AUTUMN }}>View All</div>
                    </div>
                    <div className="space-y-0">
                      {[
                        { id: '#1247', customer: 'Priya M.', amount: '\u20B92,450', status: 'Delivered', icon: Check, statusColor: '#10b981' },
                        { id: '#1246', customer: 'Anita S.', amount: '\u20B91,890', status: 'Shipped', icon: Truck, statusColor: '#3b82f6' },
                        { id: '#1245', customer: 'Riya K.', amount: '\u20B93,200', status: 'Processing', icon: Clock, statusColor: COLORS.AUTUMN },
                        { id: '#1244', customer: 'Meera D.', amount: '\u20B91,650', status: 'Delivered', icon: Check, statusColor: '#10b981' },
                        { id: '#1243', customer: 'Kavya R.', amount: '\u20B92,100', status: 'Shipped', icon: Truck, statusColor: '#3b82f6' },
                      ].map((order, i) => (
                        <div
                          key={i}
                          className="flex items-center justify-between py-2.5"
                          style={{ borderBottom: i < 4 ? '1px solid #f3f3f3' : 'none' }}
                        >
                          <div className="flex items-center gap-2.5">
                            <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ backgroundColor: `${order.statusColor}10` }}>
                              <order.icon className="w-3 h-3" style={{ color: order.statusColor }} />
                            </div>
                            <div>
                              <div className="text-[12px] font-semibold" style={{ color: COLORS.TEXT_DARK }}>{order.customer}</div>
                              <div className="text-[10px]" style={{ color: COLORS.TEXT_MUTED }}>{order.id}</div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-[12px] font-bold" style={{ color: COLORS.TEXT_DARK }}>{order.amount}</div>
                            <div className="text-[10px] font-semibold" style={{ color: order.statusColor }}>{order.status}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Dashboard features callout */}
          <StaggerWrap className="grid sm:grid-cols-3 gap-4 mt-10">
            {[
              { icon: BarChart3, title: 'Revenue Analytics', desc: 'Track daily, weekly, monthly revenue with visual charts' },
              { icon: Package, title: 'Order Management', desc: 'Manage orders from placed to delivered \u2014 all in one place' },
              { icon: Users, title: 'Customer Insights', desc: 'Know your customers \u2014 their orders, preferences, and history' },
            ].map((f, i) => (
              <StaggerChild key={i}>
                <div
                  className="bg-white rounded-xl p-5 text-center cursor-default hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
                  style={{ border: '1px solid rgba(0,0,0,0.04)' }}
                >
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center mx-auto mb-3"
                    style={{ backgroundColor: `${COLORS.AUTUMN}10` }}
                  >
                    <f.icon className="w-5 h-5" style={{ color: COLORS.AUTUMN }} />
                  </div>
                  <h4 className="text-[14px] font-bold mb-1.5" style={{ color: COLORS.TEXT_DARK }}>{f.title}</h4>
                  <p className="text-[13px]" style={{ color: COLORS.TEXT_MUTED, lineHeight: 1.5 }}>{f.desc}</p>
                </div>
              </StaggerChild>
            ))}
          </StaggerWrap>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          TEMPLATE SHOWCASE
      ══════════════════════════════════════════════ */}
      <section className="py-24 sm:py-32 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#fff' }}>
        <div className="max-w-7xl mx-auto">
          <Reveal className="text-center mb-16">
            <Heading>
              Your Brand Could<br />
              <span style={{ color: COLORS.AUTUMN, fontStyle: 'italic' }}>Look Like This</span>
            </Heading>
            <p className="text-lg mt-5 max-w-2xl mx-auto" style={{ color: COLORS.TEXT_MUTED, lineHeight: 1.7 }}>
              Choose from 25 professionally designed templates — each one built specifically for fashion brands
            </p>
          </Reveal>

          <StaggerWrap className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-7 mb-12">
            {[
              { img: tplEthnic, name: 'Heritage', style: 'Indian & Elegant' },
              { img: tplMinimal, name: 'Minimal', style: 'Clean & Modern' },
              { img: tplStreet, name: 'Streetwear', style: 'Bold & Sporty' },
              { img: tplLuxury, name: 'Luxury', style: 'Premium & Refined' },
              { img: tplTrendy, name: 'Motion', style: 'Trendy & Gen-Z' },
              { img: tplVibrant, name: 'Vibrant', style: 'Colorful & Fun' },
            ].map((tmpl, i) => (
              <StaggerChild key={i} className={i >= 3 ? 'hidden lg:block' : ''}>
                <Link to="/Register" className="block h-full">
                  <div className="group relative rounded-2xl overflow-hidden cursor-pointer h-full hover:scale-[1.02] hover:shadow-xl transition-all duration-300" style={{ backgroundColor: COLORS.LINEN }}>
                    <div className="aspect-[4/3] overflow-hidden">
                      <img
                        src={tmpl.img}
                        alt={tmpl.name}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <div className="text-[11px] font-semibold mb-1.5" style={{ color: COLORS.AUTUMN, letterSpacing: '0.08em' }}>
                        {tmpl.style}
                      </div>
                      <h3 className="text-base font-bold" style={{ color: COLORS.TEXT_DARK }}>{tmpl.name}</h3>
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-400">
                      <div
                        className="bg-white/95 backdrop-blur-sm rounded-full px-5 py-2.5 text-[13px] font-bold flex items-center gap-2 translate-y-4 group-hover:translate-y-0 transition-transform duration-300"
                        style={{ color: COLORS.TEXT_DARK }}
                      >
                        <Eye className="w-4 h-4" /> Preview
                      </div>
                    </div>
                  </div>
                </Link>
              </StaggerChild>
            ))}
          </StaggerWrap>

          <Reveal className="text-center">
            <Link to="/Register">
              <button
                className="px-8 py-3.5 rounded-xl font-bold text-[14px] inline-flex items-center gap-2 hover:scale-[1.03] active:scale-[0.97] transition-all duration-200"
                style={{ color: COLORS.AUTUMN, border: `2px solid ${COLORS.AUTUMN}`, backgroundColor: 'transparent' }}
              >
                Start Free & Explore All 25 Templates
                <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          FEATURES
      ══════════════════════════════════════════════ */}
      <section className="py-24 sm:py-32 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: COLORS.LINEN }}>
        <div className="max-w-6xl mx-auto">
          <Reveal className="text-center mb-16">
            <Heading>Everything You Need</Heading>
            <p className="text-[2.25rem] sm:text-[3rem] lg:text-[3.75rem] mt-1"
              style={{ fontFamily: SERIF, fontWeight: 400, lineHeight: 1.1, letterSpacing: '-0.02em', color: COLORS.TEXT_MUTED, opacity: 0.35 }}>
              Built Right In
            </p>
          </Reveal>

          <StaggerWrap className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: Globe, title: 'Your Own Branded URL', desc: 'yourbrand.vasstr.com \u2014 professional and memorable' },
              { icon: Smartphone, title: 'Mobile Optimized', desc: 'Looks perfect on phones, tablets, and desktops' },
              { icon: CreditCard, title: 'Razorpay Payments', desc: 'Accept payments securely \u2014 UPI, cards, netbanking' },
              { icon: Package, title: 'Product Management', desc: 'Upload unlimited products with photos, sizes, prices' },
              { icon: BarChart3, title: 'Analytics Dashboard', desc: 'Track orders, revenue, and customer insights' },
              { icon: Users, title: 'Customer Management', desc: 'See all your customers, their orders, and history' },
              { icon: Gift, title: 'Coupon System', desc: 'Create discount codes to boost your sales' },
              { icon: Instagram, title: 'SEO & Blog Tools', desc: 'Get discovered on Google with built-in SEO tools' },
              { icon: MessageCircle, title: 'WhatsApp Integration', desc: 'Customers can reach you directly via WhatsApp' },
            ].map((f, i) => (
              <StaggerChild key={i}>
                <div
                  className="bg-white rounded-2xl p-6 h-full cursor-default hover:-translate-y-1.5 hover:shadow-lg hover:scale-[1.02] transition-all duration-300"
                  style={{ border: '1px solid rgba(0,0,0,0.04)' }}
                >
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
                    style={{ backgroundColor: `${COLORS.AUTUMN}10` }}
                  >
                    <f.icon className="w-5 h-5" style={{ color: COLORS.AUTUMN }} />
                  </div>
                  <h3 className="text-[16px] font-bold mb-2" style={{ color: COLORS.TEXT_DARK, letterSpacing: '-0.01em' }}>
                    {f.title}
                  </h3>
                  <p className="text-[14px]" style={{ color: COLORS.TEXT_MUTED, lineHeight: 1.65 }}>{f.desc}</p>
                </div>
              </StaggerChild>
            ))}
          </StaggerWrap>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          PRICING
      ══════════════════════════════════════════════ */}
      <section className="py-24 sm:py-32 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#fff' }}>
        <div className="max-w-4xl mx-auto text-center">
          <Reveal>
            <Heading>Simple, Honest Pricing</Heading>
            <p className="text-lg mt-5 mb-14 max-w-2xl mx-auto" style={{ color: COLORS.TEXT_MUTED, lineHeight: 1.7 }}>
              Start free. Upgrade when you're ready. No hidden fees, no surprises.
            </p>
          </Reveal>

          <StaggerWrap className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-4xl mx-auto mb-12">
            {[
              { label: 'Free Trial', price: '\u20B90', period: '14 days', desc: 'Full access, no card needed' },
              { label: 'Monthly', price: '\u20B9999', oldPrice: '\u20B92,250', period: '/month', desc: 'All features included' },
              { label: 'Half-Yearly', price: '\u20B9899', oldPrice: '\u20B92,167', period: '/month', desc: '6-month plan \u2014 Best Value', featured: true },
              { label: 'Annual', price: '\u20B9849', oldPrice: '\u20B92,000', period: '/month', desc: '12-month plan \u2014 Max Savings' },
            ].map((plan, i) => (
              <StaggerChild key={i}>
                <div
                  className={`rounded-2xl p-6 text-center h-full hover:-translate-y-2 hover:shadow-2xl transition-all duration-300 ${plan.featured ? 'relative' : ''}`}
                  style={plan.featured
                    ? { backgroundColor: COLORS.MIDNIGHT, color: '#fff', boxShadow: `0 16px 40px -8px ${COLORS.MIDNIGHT}30` }
                    : { backgroundColor: '#fff', border: `1px solid ${COLORS.LINEN}` }
                  }
                >
                  {plan.featured && (
                    <div
                      className="text-[10px] font-bold uppercase mb-3 animate-pulse"
                      style={{ letterSpacing: '0.18em', color: COLORS.AUTUMN_LIGHT }}
                    >
                      Most Popular
                    </div>
                  )}
                  <div className="text-[11px] font-bold uppercase mb-2"
                    style={{ letterSpacing: '0.15em', color: plan.featured ? 'rgba(255,255,255,0.6)' : COLORS.AUTUMN }}>
                    {plan.label}
                  </div>
                  {plan.oldPrice && (
                    <div className="text-[14px] line-through mb-1" style={{ color: plan.featured ? 'rgba(255,255,255,0.35)' : '#9ca3af' }}>
                      {plan.oldPrice}
                    </div>
                  )}
                  <div className="text-[2.25rem] font-extrabold mb-1" style={{ letterSpacing: '-0.02em' }}>
                    {plan.price}
                  </div>
                  <div className="text-[13px] mb-3" style={{ color: plan.featured ? 'rgba(255,255,255,0.4)' : COLORS.TEXT_MUTED }}>
                    {plan.period}
                  </div>
                  <div className="text-[13px]" style={{ color: plan.featured ? 'rgba(255,255,255,0.5)' : COLORS.TEXT_MUTED }}>
                    {plan.desc}
                  </div>
                </div>
              </StaggerChild>
            ))}
          </StaggerWrap>

          <Reveal>
            <Link to="/Register">
              <button
                className="text-[14px] font-semibold hover:underline underline-offset-4 inline-flex items-center gap-1.5 hover:translate-x-1 active:scale-[0.97] transition-all duration-200"
                style={{ color: COLORS.AUTUMN }}
              >
                Start Free — See All Plans <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          LIVE STORES
      ══════════════════════════════════════════════ */}
      <section className="relative py-24 sm:py-32 px-4 sm:px-6 lg:px-8 overflow-hidden" style={{ backgroundColor: COLORS.LINEN }}>
        <div className="relative max-w-5xl mx-auto text-center">
          <Reveal>
            <Heading>Real Stores. Real Designers.</Heading>
            <p className="text-lg mt-5 mb-14 max-w-2xl mx-auto" style={{ color: COLORS.TEXT_MUTED, lineHeight: 1.7 }}>
              Fashion brands across India are already selling on Vasstr. See their live stores.
            </p>
          </Reveal>

          <StaggerWrap className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
            {[
              { name: 'Meraki', url: 'meraki.vasstr.com', img: tplMinimal },
              { name: 'Niha Creations', url: 'nihacreations.vasstr.com', img: tplEthnic },
              { name: 'Snithi Fashion', url: 'snithi.vasstr.com', img: tplVibrant },
            ].map((store, i) => (
              <StaggerChild key={i}>
                <div
                  className="group rounded-2xl overflow-hidden cursor-pointer bg-white hover:-translate-y-2 hover:shadow-xl transition-all duration-300"
                  style={{ border: `1px solid ${COLORS.AUTUMN}10` }}
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={store.img}
                      alt={store.name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-5 flex items-center justify-between">
                    <div className="text-left">
                      <div className="text-[15px] font-bold" style={{ color: COLORS.TEXT_DARK }}>{store.name}</div>
                      <div className="text-[13px] font-medium" style={{ color: COLORS.TEXT_MUTED }}>{store.url}</div>
                    </div>
                    <div
                      className="w-2.5 h-2.5 rounded-full animate-pulse"
                      style={{ backgroundColor: COLORS.AUTUMN }}
                    />
                  </div>
                </div>
              </StaggerChild>
            ))}
          </StaggerWrap>

          <Reveal>
            <p className="text-[14px] font-medium" style={{ color: COLORS.TEXT_MUTED }}>
              Your store could be next
            </p>
          </Reveal>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          FAQ — Common Questions
      ══════════════════════════════════════════════ */}
      <section className="py-24 sm:py-32 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#fff' }}>
        <div className="max-w-3xl mx-auto">
          <Reveal className="text-center mb-14">
            <Heading>
              Common Questions
            </Heading>
          </Reveal>

          <div className="space-y-4">
            {[
              { q: 'Do I need any coding or technical skills?', a: 'Not at all. Vasstr is a visual drag-and-drop builder. You pick a template, upload your products, and your store is live. Everything is point-and-click.' },
              { q: 'How do payments work? When do I get paid?', a: 'Customers pay via Razorpay (UPI, cards, netbanking). 97% goes directly to your bank account. 3% is the platform fee. Payouts happen after you complete KYC verification.' },
              { q: 'Can I use my own domain name?', a: 'Your store launches at yourbrand.vasstr.com. Custom domain support (yourbrand.com) is coming soon.' },
              { q: 'What happens after the 14-day free trial?', a: 'You choose a plan starting at just \u20B9999/month. All plans include every feature \u2014 no upsells, no hidden limits. Cancel anytime.' },
              { q: 'How is Vasstr different from Shopify or Wix?', a: 'Vasstr is built specifically for Indian fashion designers. Indian payment gateway (Razorpay), GST-compliant invoicing, Indian shipping integration, and templates designed for fashion \u2014 not generic stores.' },
              { q: 'Can I sell internationally?', a: 'Yes! Your store supports multi-currency display (20+ currencies). International customers see prices in their currency but all payments are processed in INR.' },
            ].map((faq, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <details className="group bg-white rounded-xl border border-gray-100 overflow-hidden" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
                  <summary className="flex items-center justify-between px-6 py-5 cursor-pointer list-none text-[15px] font-semibold" style={{ color: '#1a1a1a' }}>
                    {faq.q}
                    <span className="ml-4 flex-shrink-0 text-gray-400 group-open:rotate-45 transition-transform duration-200 text-xl">+</span>
                  </summary>
                  <div className="px-6 pb-5 text-[14px] leading-relaxed" style={{ color: '#6b7280' }}>
                    {faq.a}
                  </div>
                </details>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          FINAL CTA
      ══════════════════════════════════════════════ */}
      <section className="relative py-28 sm:py-36 px-4 sm:px-6 lg:px-8 overflow-hidden" style={{ backgroundColor: COLORS.LINEN }}>
        <Reveal className="relative max-w-3xl mx-auto text-center">
          <Heading>
            Ready to Build<br />
            <span style={{ color: COLORS.AUTUMN, fontStyle: 'italic' }}>Your Brand's Future?</span>
          </Heading>
          <p className="text-lg mt-5 mb-10 max-w-xl mx-auto" style={{ color: COLORS.TEXT_MUTED, lineHeight: 1.7 }}>
            Join fashion designers across India who are growing their business with their own professional website.
          </p>

          <Link to="/Register">
            <button
              className="group relative px-12 py-5 rounded-2xl font-bold text-white text-lg inline-flex items-center gap-2.5 overflow-hidden hover:scale-[1.04] active:scale-[0.97] transition-transform duration-200"
              style={{ backgroundColor: COLORS.AUTUMN, boxShadow: `0 20px 50px -10px ${COLORS.AUTUMN}40` }}
            >
              <span className="relative z-10 flex items-center gap-2.5">
                Get Your Free Website Now
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1.5 transition-transform duration-300" />
              </span>
            </button>
          </Link>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            {[
              { text: 'Free 14-day trial' },
              { text: 'No credit card' },
              { text: 'Live in 5 minutes' },
            ].map((item, i) => (
              <span
                key={i}
                className="flex items-center gap-1.5 text-[13px] font-medium"
                style={{ color: COLORS.TEXT_MUTED }}
              >
                <Check className="w-3.5 h-3.5" style={{ color: COLORS.AUTUMN }} />
                {item.text}
                {i < 2 && <span className="ml-3" style={{ color: '#ddd' }}>·</span>}
              </span>
            ))}
          </div>
        </Reveal>
      </section>

      {/* Footer */}
      <VFooter />

      {/* Video Modal */}
      {showVideoModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fadeIn"
          style={{ backgroundColor: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(10px)' }}
          onClick={() => setShowVideoModal(false)}
        >
          <div
            className="bg-white rounded-2xl p-7 max-w-2xl w-full animate-fadeUp"
            style={{ boxShadow: '0 40px 80px -20px rgba(0,0,0,0.3)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-5">
              <h3 className="text-lg font-bold" style={{ fontFamily: SERIF, color: COLORS.TEXT_DARK }}>How Vasstr Works</h3>
              <button
                onClick={() => setShowVideoModal(false)}
                className="p-2 rounded-lg hover:bg-gray-100 hover:rotate-90 transition-all duration-200"
              >
                <X className="w-5 h-5" style={{ color: COLORS.TEXT_MUTED }} />
              </button>
            </div>
            <div className="rounded-xl aspect-video flex items-center justify-center" style={{ backgroundColor: COLORS.LINEN }}>
              <div className="text-center">
                <Play className="w-16 h-16 mx-auto mb-3 animate-pulse" style={{ color: COLORS.AUTUMN }} />
                <p className="text-[14px]" style={{ color: COLORS.TEXT_MUTED }}>Demo video coming soon</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSS Keyframes */}
      <style>{`
        @keyframes fadeUp { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes marquee { 0% { transform: translateX(0%); } 100% { transform: translateX(-50%); } }
        .animate-marquee { animation: marquee 35s linear infinite; }
        .animate-fadeIn { animation: fadeIn 0.8s ease-out forwards; }
        .animate-fadeUp { animation: fadeUp 0.7s ease-out forwards; }
      `}</style>
    </div>
  );
};

export default LandingPage;
